import {
  DocumentInfo,
  Endereco,
  FuncaoParticipante,
  RepresentanteLegal,
  UsuarioParticipante,
} from 'common/genericTypes';
import { typedAction } from 'store/modules/base';

export interface RepresentanteLegalState extends RepresentanteLegal {}

interface EnderecoState extends Endereco {}

interface Contato {
  telefones: Array<string>;
}

export interface Participante {
  tipoAcesso: string;
  funcoes: Array<{ value: FuncaoParticipante; description: string }>;
  razaoSocial: string;
  cnpjParticipante: string;
  endereco: EnderecoState;
  contato: Contato;
  atividadePrincipal: string;
  faturamento: number | null;
  razaoSocialBancoLiquidante: string;
  ispbBancoLiquidante: string;
  agenciaBancoLiquidante: string;
  contaBancoLiquidante: string;
  documentos: Array<DocumentInfo>;
  representantesLegais: Array<RepresentanteLegalState>;
  usuarios: Array<UsuarioParticipante>;
  documentosExcluir: Array<DocumentInfo>;
  inscricaoEstadual: string;
  inscricaoMunicipal: string;
  grupoEconomico: string;
}

interface EnderecoField {
  name: keyof EnderecoState;
  value: string;
}

interface ContatoField {
  name: keyof Contato;
  value: Array<string>;
}

interface Field {
  name: keyof Omit<Participante, 'documentos' | 'endereco' | 'contato'>;
  value: string | number | null | Array<string> | Array<{ value: FuncaoParticipante; description: string }> | boolean;
}

const initialState: Participante = {
  tipoAcesso: 'PARTICIPANTE',
  funcoes: [],
  razaoSocial: '',
  cnpjParticipante: '',
  endereco: {
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
  },
  contato: {
    telefones: [],
  },
  atividadePrincipal: '',
  faturamento: null,
  razaoSocialBancoLiquidante: '',
  ispbBancoLiquidante: '',
  agenciaBancoLiquidante: '',
  contaBancoLiquidante: '',
  documentos: [],
  representantesLegais: [],
  documentosExcluir: [],
  usuarios: [],
  inscricaoEstadual: '',
  inscricaoMunicipal: '',
  grupoEconomico: '',
};

export const updateField = (field: Field) => {
  return typedAction('participante/UPDATEFIELD', field);
};

export const updateEndereco = (field: EnderecoField) => {
  return typedAction('participante/endereco/UPDATE', field);
};

export const updateContato = (field: ContatoField) => {
  return typedAction('participante/contato/UPDATE', field);
};

export const updateDocumentos = (documents: DocumentInfo[]) => {
  return typedAction('participante/documentos/UPDATE', documents);
};

export const updateDocumentosExcluir = (documents: DocumentInfo[]) => {
  return typedAction('participante/documentosExcluir/UPDATE', documents);
};

export const updateRepresentantes = (representantes: RepresentanteLegalState[]) => {
  return typedAction('participante/representantes/UPDATE', representantes);
};

export const updateUsuarios = (usuarios: UsuarioParticipante[]) => {
  return typedAction('participante/usuarios/UPDATE', usuarios);
};

export const updateParticipante = (participante: Participante) => {
  return typedAction('participante/UPDATE', participante);
};

export const resetParticipante = () => {
  return typedAction('participante/RESET');
};

type registroAction = ReturnType<
  | typeof updateField
  | typeof resetParticipante
  | typeof updateEndereco
  | typeof updateContato
  | typeof updateDocumentos
  | typeof updateDocumentosExcluir
  | typeof updateParticipante
  | typeof updateRepresentantes
  | typeof updateUsuarios
>;

export function participanteReducer(
  state: Participante = {
    ...initialState,
  },
  action: registroAction
): Participante {
  switch (action.type) {
    case 'participante/UPDATEFIELD':
      return { ...state, [action.payload.name]: action.payload.value };
    case 'participante/endereco/UPDATE':
      return { ...state, endereco: { ...state.endereco, [action.payload.name]: action.payload.value } };
    case 'participante/contato/UPDATE':
      return { ...state, contato: { ...state.contato, [action.payload.name]: action.payload.value } };
    case 'participante/documentos/UPDATE':
      return { ...state, documentos: action.payload };
    case 'participante/documentosExcluir/UPDATE':
      return { ...state, documentosExcluir: action.payload };
    case 'participante/representantes/UPDATE':
      return { ...state, representantesLegais: action.payload };
    case 'participante/usuarios/UPDATE':
      return { ...state, usuarios: action.payload };
    case 'participante/UPDATE':
      return { ...action.payload };
    case 'participante/RESET':
      return initialState;
    default:
      return state;
  }
}
